import api from "@/api";
import { getField, updateField } from "vuex-map-fields";
import dayjs from "dayjs";

import documentsApi from "@/api/documents";
import documentVersionsApi from "@/api/documentVersions";

import saveAs from "file-saver";

function hasProp(obj, prop) {
  return Object.prototype.hasOwnProperty.call(obj, prop);
}

function initialState() {
  return {
    loading: false,
    docType: null,
    documents: { forms: [], policies: [] },
    currentDocument: null,
    documentVersions: {},
    loadingDocVersions: false,
    user_filter: false,
    responseData: {
      fileId: null,
    },
    filters: {
      categories: [],
      searchQuery: "",
      status: "approved",
    },
    sortBy: null,
    tabFilter: "approved",
    favourites: [],
    currentVersion: null,
    pagination: {
      total: 12,
      per_page: 12,
      current_page: 1,
      last_page: 1,
      from: 1,
      to: 12,
    },
    searchResults: [],
  };
}

export default {
  namespaced: true,

  state: initialState,

  getters: {
    document: (state) => state.currentDocument,

    dataLoaded(state) {
      return !!state.documents;
    },

    versions: (state) => (document) => {
      if (state.documentVersions[document.id]) {
        const docVersions = state.documentVersions[document.id];
        const filtered = docVersions.filter((version) => {
          if (version.id !== document.current_version.id) {
            return version;
          }
          return null;
        });
        return filtered.sort((a, b) => b.version - a.version);
      }
      return [];
    },

    formattedSearchResults(state) {
      if (!state.searchResults || state.searchResults.length < 1) {
        return null;
      }
      const origResults = state.searchResults;
      let returnResults = {};
      if (origResults.content) {
        const returnContent = origResults.content.map((cont) => ({
          id: cont.page_url,
          pageId: cont.id,
          name: cont.title,
          type: "page",
        }));

        if (returnContent.length > 0) {
          returnResults = {
            content: returnContent,
          };
        }
      }

      if (origResults.policies) {
        const returnPolicies = origResults.policies
          .filter((doc) => doc.current_version_id)
          .map((document) => ({
            id: `file/${document.current_version_id}`,
            name: document.current_version.title,
            documentId: document.current_version.policy_id,
            type: "policies",
          }));

        if (returnPolicies.length > 0) {
          returnResults = {
            ...returnResults,
            policies: returnPolicies,
          };
        }
      }

      if (origResults.eforms) {
        const returnForms = origResults.eforms
          .filter((doc) => doc.current_version.id)
          .map((form) => ({
            id: `/form${form.eform_url}`,
            name: form.current_version.title,
            type: "eform",
          }));

        if (returnForms.length > 0) {
          returnResults = {
            ...returnResults,
            eforms: returnForms,
          };
        }
      }

      if (origResults.surveys) {
        const returnSurveys = origResults.surveys
          .filter((doc) => doc.current_version.id)
          .map((survey) => ({
            id: `/survey${survey.survey_url}`,
            name: survey.current_version.title,
            type: "survey",
          }));

        if (returnSurveys.length > 0) {
          returnResults = {
            ...returnResults,
            surveys: returnSurveys,
          };
        }
      }

      if (origResults.polls) {
        console.log(
          "TCL:  ~ file: documents.js ~ line 145 ~ formattedSearchResults ~ origResults.polls",
          origResults.polls
        );
        const returnPolls = origResults.polls.map((poll) => ({
          id: poll.id,
          name: poll.title,
          type: "poll",
        }));

        if (returnPolls.length > 0) {
          returnResults = {
            ...returnResults,
            polls: returnPolls,
          };
        }
      }

      return returnResults;
    },

    getField,
  },

  mutations: {
    SET_FILTER_TOTAL(state, total) {
      state.pagination.total = total;
      state.pagination.last_page = Math.ceil(total / state.pagination.per_page);
    },
    SET_CURRENT_PAGE(state, page) {
      state.pagination.current_page = page;
    },
    SET_CURRENT_VERSION(state, payload) {
      state.currentVersion = payload;
    },
    setDocuments(state, documents) {
      const docType = state.docType.plural;
      state.documents[docType] = documents;
    },

    setCurrentDocument(state, currentDocument) {
      state.currentDocument = Object.assign({}, currentDocument);
    },

    setFileId(state, fileId) {
      state.responseData.fileId = fileId;
    },

    setSortBy(state, { direction, field }) {
      // set direction to 'asc' if not set
      const newDirection = direction || "asc";

      state.sortBy.direction = newDirection;
      state.sortBy.field = field;
    },

    setDocType(state, docType) {
      if (docType === "forms") {
        state.apiEndpoint = "/formmanagement";
        state.sortBy = {
          label: "Name (A-Z)",
          field: "form_title.raw",
          direction: "asc",
        };
        state.docType = {
          singular: "form",
          plural: "forms",
        };
      }

      if (docType === "policies") {
        state.apiEndpoint = "/policymanagement";
        state.sortBy = {
          label: "Name (A-Z)",
          field: "policy_title.raw",
          direction: "asc",
        };
        state.docType = {
          singular: "policy",
          plural: "policies",
        };
      }
    },

    setSearchQuery(state, searchQuery) {
      state.filters.searchQuery = searchQuery;
    },

    setFilterCategories(state, categories) {
      state.filters.categories = categories;
    },

    reset(state) {
      // acquire initial state
      const s = initialState();
      Object.keys(s).forEach((key) => {
        if (
          typeof s[key] === "object" &&
          s[key] === Object(s[key]) &&
          s[key] !== null
        ) {
          state[key] = Object.assign({}, s[key]);
        } else {
          state[key] = s[key];
        }
      });
    },

    clearFilters(state) {
      state.filters.categories = [];
      state.filters.searchQuery = "";
    },

    setFavourites(state, favourites) {
      state.favourites = favourites;
    },

    setTabFilter(state, tabFilter) {
      state.tabFilter = tabFilter;
    },

    addDocumentVersions(state, { documentId, versions }) {
      const docVersions = Object.assign({}, state.documentVersions);
      docVersions[documentId] = versions;
      state.documentVersions = Object.assign({}, docVersions);
    },

    SET_POLICIES(state, payload) {
      state.documents.policies = payload;
    },
    SET_FORMS(state, payload) {
      state.documents.forms = payload;
    },
    SET_USER_FILTER(state, val) {
      state.user_filter = val;
    },
    SET_SEARCH_RESULTS(state, payload) {
      state.searchResults = payload;
    },

    setPage(state, page) {
      console.log("🚀 ~ file: documents.js ~ line 246 ~ setPage ~ page", page);
      state.pagination.current_page = page;
    },

    updateField,
  },

  actions: {
    reset({ commit }) {
      commit("reset");
    },

    init({ commit }, { docType }) {
      return new Promise((resolve) => {
        commit("setDocType", docType);
        resolve();
      });
    },

    clearSearch({ commit }) {
      commit("SET_SEARCH_RESULTS", null);
    },

    // DOCUMENT CRUD ACTIONS

    /**
     * Pending and rejected need to go through versions, not documents
     */
    list({ commit, state }) {
      commit("setDocuments", []);
      state.loading = true;

      const page = state.pagination.current_page;

      const { tabFilter } = state;

      let listFavourites = false;

      const size = state.pagination.per_page;
      let from = size * page - size;

      if (!from) {
        from = 0;
      }

      const statusFilter =
        tabFilter && tabFilter !== "starred" ? tabFilter : "approved";

      const filter = {
        sort: [
          {
            [state.sortBy.field]: {
              order: state.sortBy.direction,
            },
          },
        ],
        size,
        from,
        query: {
          bool: {
            must: [
              {
                bool: {
                  should: [
                    {
                      match: {
                        [`${state.docType.singular}_title`]: {
                          query: state.filters.searchQuery,
                          operator: "and",
                          zero_terms_query: "all",
                          fuzziness: "AUTO",
                        },
                      },
                    },
                    {
                      match: {
                        tag_names: {
                          query: state.filters.searchQuery,
                          operator: "and",
                          zero_terms_query: "all",
                          fuzziness: "AUTO",
                        },
                      },
                    },
                  ],
                },
              },
            ],
          },
        },
      };

      if (statusFilter) {
        // review and lapsed need to be date based
        switch (statusFilter) {
          case "review":
            filter.query.bool.must.push([
              {
                range: {
                  review_date: {
                    gte: dayjs().unix(),
                    lte: dayjs()
                      .add(14, "day")
                      .unix(),
                  },
                },
              },
              {
                term: {
                  status: "approved",
                },
              },
            ]);
            break;
          case "lapsed":
            filter.query.bool.must.push([
              {
                range: {
                  review_date: {
                    lte: dayjs().unix(),
                  },
                },
              },
              {
                term: {
                  status: "approved",
                },
              },
            ]);
            break;
          case "archived":
            filter.query.bool.must.push([
              {
                term: {
                  archived: true,
                },
              },
            ]);
            break;
          // everything else is status
          default:
            filter.query.bool.must.push({
              term: {
                status: statusFilter,
              },
            });
            break;
        }
      }

      if (state.filters.categories.length >= 1) {
        filter.query.bool.must.push({
          terms: {
            categories: state.filters.categories,
          },
        });
      }

      if (tabFilter === "starred") {
        listFavourites = true;
      }

      /**
       * If there is a tab filter, and we are admin, get everything
       * Else retrict to what the user id is.
       * IF we are restricting by the user, then obviously it depends on the tab
       */
      if (state.user_filter) {
        const submittedByParam = ["pending", "rejected"];

        if (submittedByParam.includes(statusFilter)) {
          filter.query.bool.must.push({
            term: {
              created_user_id: state.user_filter,
            },
          });
        } else {
          filter.query.bool.must.push({
            bool: {
              should: [
                {
                  term: {
                    primary_user_id: state.user_filter,
                  },
                },
                {
                  term: {
                    secondary_user_id: state.user_filter,
                  },
                },
              ],
            },
          });
        }
      }

      const versionChecks = ["pending", "rejected", "lapsed", "review"];

      if (versionChecks.includes(statusFilter)) {
        return new Promise((resolve, reject) => {
          api()
            .put(`/${state.docType.singular}management/versions`, {
              filter,
            })
            .then((documents) => {
              const returnDocs = documents.data.data.version.map((doc) => ({
                ...doc,
                current_version: doc,
              }));
              commit("SET_FILTER_TOTAL", documents.data.meta.filter_total);
              commit("SET_CURRENT_PAGE", page);
              commit("setDocuments", returnDocs);
              resolve(returnDocs);
            })
            .catch((error) => {
              console.error(error);
              reject(error);
            })
            .finally(() => {
              setTimeout(() => {
                state.loading = false;
              }, 100);
            });
        });
      }

      // if we are not archived, we need to hide archived posts
      if (statusFilter !== "archived") {
        filter.query.bool.must_not = [
          {
            term: {
              archived: true,
            },
          },
        ];
      }
      return new Promise((resolve, reject) => {
        documentsApi
          .list(state.docType, filter, listFavourites)
          .then(({ documents, filterTotal }) => {
            commit("SET_FILTER_TOTAL", filterTotal);
            commit("SET_CURRENT_PAGE", page);
            commit("setDocuments", documents);
            resolve(documents);
          })
          .catch((error) => {
            console.error(error);
            reject(error);
          })
          .finally(() => {
            setTimeout(() => {
              state.loading = false;
            }, 100);
          });
      });
    },

    // get content, forms and policies, limit by 10 though and filter by name/title
    async listAllForSelect(
      { commit },
      {
        query,
        includeForms,
        includeContent,
        includePolicies,
        includeEforms,
        includeSurveys,
      }
    ) {
      const filterTerm = query;
      const returnArr = {};
      const limit = 5;

      // content first
      if (includeContent) {
        const contentQuery = {
          query: {
            bool: {
              must: [
                {
                  terms: {
                    status: ["approved"],
                  },
                },
              ],
            },
          },
        };

        if (filterTerm) {
          contentQuery.query.bool.must.push({
            bool: {
              should: [
                {
                  match: {
                    title: {
                      query: filterTerm,
                      operator: "and",
                      zero_terms_query: "all",
                      fuzziness: "AUTO",
                    },
                  },
                },
                {
                  match_phrase_prefix: {
                    title: {
                      query: filterTerm,
                      slop: 50,
                    },
                  },
                },
              ],
            },
          });
        }

        contentQuery.size = limit;
        contentQuery.from = 0;
        returnArr.content = null;
        try {
          const contentResults = await api().put("/content/pages/flat", {
            filter: contentQuery,
          });
          returnArr.content = contentResults.data.data.page;
        } catch (err) {
          delete returnArr.content;
        }

        // surveys
        const surveyQuery = {
          query: {
            bool: {
              must: [
                {
                  terms: {
                    status: ["approved"],
                  },
                },
              ],
            },
          },
        };

        if (filterTerm) {
          surveyQuery.query.bool.must.push({
            match_phrase_prefix: {
              survey_title: {
                query: filterTerm,
                slop: 10,
              },
            },
          });
        }

        surveyQuery.size = limit;
        surveyQuery.from = 0;
        returnArr.surveys = null;
        try {
          // TODO: Switch to flat when title is coming through
          const surveyResults = await api().put(
            "/surveymanagement/surveys/flat",
            {
              filter: surveyQuery,
            }
          );
          returnArr.surveys = surveyResults.data.data.survey;
        } catch (err) {
          delete returnArr.surveys;
        }

        // polls
        const pollQuery = {
          query: {
            bool: {
              must: [
                // {
                // 	terms: {
                // 		status: ['approved'],
                // 	},
                // },
              ],
            },
          },
        };

        if (filterTerm) {
          pollQuery.query.bool.must.push({
            match_phrase_prefix: {
              poll_title: {
                query: filterTerm,
                slop: 10,
              },
            },
          });
        }

        pollQuery.size = limit;
        pollQuery.from = 0;
        returnArr.polls = null;
        try {
          // TODO: Switch to flat when title is coming through
          const pollResults = await api().put("/surveymanagement/polls/flat", {
            filter: pollQuery,
          });
          returnArr.polls = pollResults.data.data.poll;
        } catch (err) {
          delete returnArr.polls;
        }
      }

      if (includeEforms) {
        // E-Forms - yes I know it has its own store but still
        const eformQuery = {
          query: {
            bool: {
              must: [
                {
                  terms: {
                    status: ["approved"],
                  },
                },
              ],
            },
          },
        };

        if (filterTerm) {
          eformQuery.query.bool.must.push({
            match_phrase_prefix: {
              eform_title: {
                query: filterTerm,
                slop: 10,
              },
            },
          });
        }

        eformQuery.size = limit;
        eformQuery.from = 0;
        returnArr.eforms = null;
        try {
          // TODO: Switch to flat when title is coming through
          const formResults = await api().put("/eformmanagement/eforms/flat", {
            filter: eformQuery,
          });
          returnArr.eforms = formResults.data.data.eform;
        } catch (err) {
          delete returnArr.eforms;
        }
      }
      if (includeSurveys) {
        // E-Forms - yes I know it has its own store but still
        const eformQuery = {
          query: {
            bool: {
              must: [
                {
                  terms: {
                    status: ["approved"],
                  },
                },
              ],
            },
          },
        };

        if (filterTerm) {
          eformQuery.query.bool.must.push({
            match_phrase_prefix: {
              survey_title: {
                query: filterTerm,
                slop: 10,
              },
            },
          });
        }

        eformQuery.size = limit;
        eformQuery.from = 0;
        returnArr.surveys = null;
        try {
          // TODO: Switch to flat when title is coming through
          const formResults = await api().put(
            "/surveymanagement/surveys/flat",
            {
              filter: eformQuery,
            }
          );
          returnArr.surveys = formResults.data.data.survey;
        } catch (err) {
          delete returnArr.surveys;
        }
      }

      if (includePolicies) {
        // then we need to get policies
        // hide archived ones
        const policyQuery = {
          query: {
            bool: {
              must: [],
              must_not: [
                {
                  term: {
                    archived: true,
                  },
                },
              ],
            },
          },
        };

        if (filterTerm) {
          policyQuery.query.bool.must.push({
            match_phrase_prefix: {
              policy_title: {
                query: filterTerm,
                slop: 10,
              },
            },
          });
        }

        policyQuery.size = limit;
        policyQuery.from = 0;
        returnArr.policies = null;
        try {
          const policyResults = await api().put(
            "/policymanagement/policies/flat",
            {
              filter: policyQuery,
            }
          );
          console.log(
            "TCL:  ~ file: documents.js ~ line 610 ~ policyResults",
            policyResults
          );
          returnArr.policies = policyResults.data.data.policy;
        } catch (err) {}
      }
      if (includeForms) {
        returnArr.forms = [];
        try {
          // and finally forms
          const formResults = await api().put("/formmanagement/forms/flat", {
            filter: contentQuery,
          });
          returnArr.forms = formResults.data.data.form;
        } catch (err) {}
      }

      commit("SET_SEARCH_RESULTS", returnArr);
      return Promise.resolve(returnArr);
    },

    create({ state, dispatch }, documentData) {
      const docType = state.docType.singular;
      return new Promise((resolve, reject) => {
        documentsApi
          .create(state.docType, documentData)
          .then((document) => {
            dispatch(
              "notifications/create",
              {
                body: `The ${docType} was created`,
                type: "success",
              },
              { root: true }
            );

            resolve(document);
          })
          .catch((error) => {
            dispatch(
              "notifications/create",
              {
                title: "Error!",
                body: `The ${docType} failed to be created`,
                type: "danger",
              },
              { root: true }
            );

            reject(error);
          });
      });
    },

    read({ commit, state }, documentId) {
      commit("setCurrentDocument", {});

      return new Promise((resolve, reject) => {
        documentsApi
          .read(state.docType, documentId)
          .then((document) => {
            console.log("TCL: read -> document", document);
            commit("setCurrentDocument", document);
            resolve(document);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },

    delete({ state, dispatch }, documentId) {
      return new Promise((resolve, reject) => {
        documentsApi
          .delete(state.docType, documentId)
          .then((document) => {
            dispatch(
              "notifications/create",
              {
                body: `The ${state.docType.singular} was deleted`,
                type: "success",
              },
              { root: true }
            );

            dispatch("list");
            resolve(document);
          })
          .catch((error) => {
            dispatch(
              "notifications/create",
              {
                title: "Error!",
                body: `The ${state.docType.singular} could not be deleted`,
                type: "danger",
              },
              { root: true }
            );
            reject(error);
          });
      });
    },

    listVersions({ commit, state }, documentId) {
      state.loadingDocVersions = true;
      return new Promise((resolve, reject) => {
        documentVersionsApi
          .list(state.docType, documentId)
          .then((versions) => {
            commit("addDocumentVersions", { documentId, versions });
            resolve(versions);
            state.loadingDocVersions = false;
          })
          .catch((error) => {
            reject(error);
          });
      });
    },

    createVersion({ state, dispatch }, { documentId, version }) {
      const { apiEndpoint } = state;
      const docType = state.docType.singular;

      const requestEndpoint = `${apiEndpoint}/${docType}/${documentId}/version`;
      const requestBody = { version, past_date_bypass: true };

      return new Promise((resolve, reject) => {
        api()
          .put(requestEndpoint, requestBody)
          .then((response) => {
            if (response.data.data) {
              const documentVersion = response.data.data.version[0];
              resolve(documentVersion);
              // Send notification
              dispatch(
                "notifications/create",
                {
                  title: "Document Version Created",
                  body: "The document version was created",
                  type: "success",
                },
                { root: true }
              );
            } else {
              reject(response.data);
            }
          })
          .catch((error) => {
            if (
              error.response &&
              hasProp(error.response.data.data, "version")
            ) {
              const documentVersion = error.response.data.data.version[0];
              resolve(documentVersion);
              // Send notification
              dispatch(
                "notifications/create",
                {
                  title: "Document Version Created",
                  body: "The document version was created",
                  type: "success",
                },
                { root: true }
              );
            } else {
              reject(error.response.data);
            }
          });
      });
    },

    updateVersion({ state, dispatch }, { documentId, versionId, version }) {
      const { apiEndpoint } = state;
      const docType = state.docType.singular;

      const requestEndpoint = `${apiEndpoint}/${docType}/${documentId}/version/${versionId}`;
      const requestBody = { version, past_date_bypass: true };

      return new Promise((resolve, reject) => {
        api()
          .put(requestEndpoint, requestBody)
          .then((response) => {
            if (response.data.data) {
              const documentVersion = response.data.data.version[0];
              resolve(documentVersion);
              // Send notification
              dispatch(
                "notifications/create",
                {
                  title: "Document Version Updated",
                  body: "The document version was updated",
                  type: "success",
                },
                { root: true }
              );
            } else {
              reject(response.data);
            }
          })
          .catch((error) => {
            if (
              error.response &&
              hasProp(error.response.data.data, "version")
            ) {
              const documentVersion = error.response.data.data.version[0];
              resolve(documentVersion);
              // Send notification
              dispatch(
                "notifications/create",
                {
                  title: "Document Version Updated",
                  body: "The document version was updated",
                  type: "success",
                },
                { root: true }
              );
            } else {
              reject(error.response.data);
            }
          });
      });
    },

    approveDocument({ state, dispatch }, { documentId, versionId }) {
      return new Promise((resolve, reject) => {
        documentVersionsApi
          .approve(state.docType, documentId, versionId)
          .then((version) => {
            dispatch(
              "notifications/create",
              {
                body: `The ${state.docType.singular} was approved`,
                type: "success",
              },
              { root: true }
            );
            resolve(version);
          })
          .catch((error) => {
            dispatch(
              "notifications/create",
              {
                title: "Error!",
                body: `The ${state.docType.singular} could not be approved`,
                type: "danger",
              },
              { root: true }
            );
            reject(error);
          });
      });
    },

    rejectDocument({ state, dispatch }, { documentId, versionId, reason }) {
      return new Promise((resolve, reject) => {
        documentVersionsApi
          .reject(state.docType, documentId, versionId, reason)
          .then((version) => {
            dispatch(
              "notifications/create",
              {
                body: `The ${state.docType.singular} was rejected`,
                type: "success",
              },
              { root: true }
            );
            resolve(version);
          })
          .catch((error) => {
            dispatch(
              "notifications/create",
              {
                title: "Error!",
                body: `The ${state.docType.singular} could not be rejected`,
                type: "danger",
              },
              { root: true }
            );
            reject(error);
          });
      });
    },

    async getVersion({ state, commit }, { documentId, versionId }) {
      let resp;

      try {
        resp = await api().get(
          `/${state.docType.singular}management/${state.docType.singular}/${documentId}/version/${versionId}`
        );
        commit("SET_CURRENT_VERSION", resp.data.data.version[0]);
        return Promise.resolve(resp.data.data.version[0]);
      } catch (err) {
        return Promise.reject(err);
      }
    },

    favourite({ state, dispatch }, documentId) {
      documentsApi.favourite(state.docType, documentId).then((document) => {
        dispatch(
          "notifications/create",
          {
            body: `The ${state.docType.singular} <b>${document.current_version.title}</b> has been favorited`,
            type: "success",
          },
          { root: true }
        );
      });
    },

    unfavourite({ state }, documentId) {
      documentsApi.unfavourite(state.docType, documentId);
    },

    createAndPublish({ state, dispatch }, documentData) {
      const version = {
        title: documentData.title,
        version: documentData.starting_version,
        file_id: state.responseData.fileId,
        external_url: documentData.external_resource,
      };

      if (documentData.review_date) {
        version.review_date = documentData.review_date;
      }

      return new Promise((resolve, reject) => {
        dispatch("create", documentData)
          .then((document) => {
            dispatch("createVersion", { documentId: document.id, version })
              .then((documentVersion) => {
                resolve({
                  document,
                  version: documentVersion,
                });
              })
              .catch((error) => reject(error));
          })
          .catch((error) => reject(error));
      });
    },

    /**
     * This is for just updating details about the document, not version
     */
    updateDocument({ state }, documentData) {
      const payload = {};
      payload[state.docType.singular] = documentData;

      return new Promise((resolve, reject) => {
        api()
          .post(
            `${state.apiEndpoint}/${state.docType.singular}/${documentData.id}/`,
            payload
          )
          .then((document) => {
            resolve(document);
          })
          .catch((error) => {
            console.log("error", { ...error });
            reject(error);
          });
      });
    },

    update({ state, dispatch }, {documentData, skipVersion}) {
      const version = {
        title: documentData.title,
        version:
          documentData.new_version || documentData.current_version.version,
        external_url:
          documentData.external_resource ||
          documentData.current_version.external_url,
      };

      if (documentData.newFileId) {
        version.file_id = documentData.newFileId;
        version.external_url = null; // file trumps url
      } else {
        version.file_id = documentData.current_version.file
          ? documentData.current_version.file.id
          : null;
      }

      if (documentData.review_date) {
        version.review_date = documentData.review_date;
      }

      let vAction = "updateVersion";
      let vPayload = {
        documentId: documentData.id,
        versionId: documentData.current_version.id,
        version,
      };

      /**
       * If we just updating, we need to go to updateVersion
       * If we have a new file and new version number, we need to go to createVersion
       */

      if (
        (documentData.current_version.version &&
          documentData.current_version.version !==
            version.version.toString()) ||
        (documentData.current_version.file &&
          documentData.current_version.file.id !== version.file_id) ||
        (documentData.current_version.external_url !== "" && typeof documentData.current_version.external_url !== "undefined")
      ) {
        vAction = "createVersion";
        vPayload = { documentId: documentData.id, version };
      }

      const payload = {};
      payload[state.docType.singular] = documentData;

      return new Promise((resolve, reject) => {
        api()
          .post(
            `${state.apiEndpoint}/${state.docType.singular}/${documentData.id}`,
            payload
          )
          .then((document) => {
            if (!skipVersion) {
              dispatch(vAction, vPayload)
                .then((documentVersion) => {
                  resolve({
                    document,
                    version: documentVersion,
                  });
                })
                .catch((error) => reject(error));
            } else {
              resolve({
                document,
              })
            }
          })
          .catch((error) => {
            console.log("error", { ...error });
            reject(error);
          });
      });
    },

    async getUserDocCount({ state, rootState, dispatch }) {
      let userId;

      if (!rootState.auth.currentUser.id) {
        await dispatch("auth/listCurrentUser", {}, { root: true }).then(
          (resp) => {
            userId = resp.id;
          }
        );
      } else {
        userId = rootState.auth.currentUser.id;
      }

      const payload = {
        filter: {
          query: {
            bool: {
              should: [
                {
                  term: {
                    primary_user_id: userId,
                  },
                },
                {
                  term: {
                    created_user_id: userId,
                  },
                },
                {
                  term: {
                    secondary_user_id: userId,
                  },
                },
              ],
            },
          },
        },
        size: 1,
        from: 0,
      };

      let response;

      try {
        response = await api().put(
          `/${state.docType.singular}management/${state.docType.plural}`,
          payload
        );
        return Promise.resolve(response.data.meta.filter_total);
      } catch (err) {
        return Promise.resolve(0);
      }
    },

    async archiveDocument({ state }, redirectId) {
      let response;

      const { currentDocument } = state;

      try {
        response = await api().put(
          `/${state.docType.singular}management/${state.docType.singular}/${currentDocument.id}/archive`,
          {
            archive: {
              archive_redirect_policy_id: redirectId,
            },
          }
        );
        console.log(
          "TCL:  ~ file: documents.js ~ line 1106 ~ archiveDocument ~ response",
          response
        );
        return Promise.resolve(response);
      } catch (err) {
        console.log(
          "TCL:  ~ file: documents.js ~ line 1108 ~ archiveDocument ~ err",
          err
        );
        return Promise.reject(err);
      }
    },

    downloadReport({ commit, state }, isAdmin) {
      return new Promise((resolve, reject) => {
        documentsApi
          .downloadReport(state.docType, isAdmin)
          .then((response) => {
            const blob = new Blob([response.data], {
              type: response.headers["content-type"],
            });
            saveAs(blob, `${state.docType.singular}_report.xlsx`);
            resolve();
          })
          .catch((error) => {
            reject("report download error: ", error);
          });
      });
    },
  },
};
